/**
 * Centralized logging utility for Vue frontend
 * Provides environment-based logging with structured output
 *
 * @format
 */

import { reportError } from "./errorReporting";

type LogLevel = "debug" | "info" | "warn" | "error";

interface LoggerConfig {
  level: LogLevel;
  enabled: boolean;
}

class Logger {
  private config: LoggerConfig;

  constructor() {
    this.config = this.getConfig();
  }

  private getConfig(): LoggerConfig {
    const env = import.meta.env.MODE || "development";
    const customLevel = import.meta.env.VITE_LOG_LEVEL as LogLevel;

    return {
      level: customLevel || (env === "production" ? "warn" : "debug"),
      enabled:
        env !== "production" || Boolean(import.meta.env.VITE_ENABLE_LOGGING),
    };
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) return false;

    const levels: LogLevel[] = ["debug", "info", "warn", "error"];
    const currentLevelIndex = levels.indexOf(this.config.level);
    const messageLevelIndex = levels.indexOf(level);

    return messageLevelIndex >= currentLevelIndex;
  }

  private formatMessage(level: LogLevel, message: unknown, ...args: unknown[]): void {
    if (!this.shouldLog(level)) return;

    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    switch (level) {
      case "error":
        console.error(prefix, message, ...args);
        break;
      case "warn":
        console.warn(prefix, message, ...args);
        break;
      case "info":
        console.info(prefix, message, ...args);
        break;
      case "debug":
      default:
        console.log(prefix, message, ...args);
        break;
    }
  }

  debug(message: unknown, ...args: unknown[]): void {
    this.formatMessage("debug", message, ...args);
  }

  info(message: unknown, ...args: unknown[]): void {
    this.formatMessage("info", message, ...args);
  }

  warn(message: unknown, ...args: unknown[]): void {
    this.formatMessage("warn", message, ...args);
    this.report("warn", message, args);
  }

  error(message: unknown, ...args: unknown[]): void {
    this.formatMessage("error", message, ...args);
    this.report("error", message, args);
  }

  // Forward warn/error to the API so existing logger calls become durable.
  // Independent of console gating: reports even when console is silenced.
  private report(level: "warn" | "error", message: unknown, args: unknown[]): void {
    const err = args.find((a): a is Error => a instanceof Error);
    reportError({
      message: typeof message === "string" ? message : String(message),
      level,
      stack: err?.stack,
      context: args.length ? { args } : undefined,
    });
  }
}

export const logger = new Logger();
export default logger;
